import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, List, LogIn, Key, ShieldCheck, HelpCircle, Layout, Zap, Smartphone, Settings, Info, Lock, Eye, AlertTriangle, Globe } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Como Inserir Dados de Acesso no IPTV", href: "/blog/como-inserir-dados-acesso-iptv" }
  ];

  const faq = [
    {
      question: "O que é uma URL de servidor no IPTV?",
      answer: "A URL do servidor (ou Host) é o ponto de conexão entre o seu aplicativo e o banco de dados de streaming. É através dela que o player sabe de onde buscar os canais e filmes."
    },
    {
      question: "Por que o login falha mesmo com dados corretos?",
      answer: "Geralmente é causado por espaços invisíveis copiados do WhatsApp, DNS do provedor bloqueando a URL ou excesso de conexões simultâneas permitidas no plano."
    },
    {
      question: "Diferença entre Usuário e Nome da Lista?",
      answer: "Usuário é a credencial técnica. Nome da Lista é apenas um apelido que você dá para organizar seus acessos dentro do aplicativo."
    },
    {
      question: "Meus dados expiram?",
      answer: "Sim, os dados estão vinculados à validade da sua assinatura. Após o vencimento, o servidor negará o acesso até que a renovação seja processada."
    }
  ];

  const benefits = [
    {
      title: "Segurança de Acesso",
      description: "Protocolos de autenticação que protegem sua assinatura contra clones.",
      icon: <Lock className="w-6 h-6" />
    },
    {
      title: "Agilidade Técnica",
      description: "Métodos de entrada que carregam sua lista em tempo recorde.",
      icon: <Zap className="w-6 h-6" />
    },
    {
      title: "Transparência Total",
      description: "Saiba exatamente o que cada campo de login representa no sistema.",
      icon: <Eye className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Recebimento Seguro",
      description: "Acesse seus dados via e-mail ou central do cliente protegida."
    },
    {
      title: "Limpeza de Campos",
      description: "Remova qualquer informação antiga ou espaços antes de digitar."
    },
    {
      title: "Entrada Cirúrgica",
      description: "Preencha cada campo respeitando a sintaxe técnica exata."
    },
    {
      title: "Validação Final",
      description: "Confirme o acesso e verifique a validade da sua linha."
    }
  ];

  const diferenciais = [
    {
      title: "URLs Customizadas",
      description: "Servidores com endereços curtos que facilitam a digitação no controle remoto.",
      icon: <Key className="w-6 h-6" />
    },
    {
      title: "Backup de Login",
      description: "Nossos apps oficiais salvam suas credenciais de forma segura na nuvem.",
      icon: <ShieldCheck className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. A Anatomia Crítica dos Dados de Acesso",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Inserir os dados de acesso é o ponto onde a maioria dos usuários iniciantes encontra dificuldades. Não se trata apenas de preencher um formulário comum; é uma autenticação técnica em um servidor de alta performance. Qualquer erro, por menor que seja, resultará em uma falha de handshake entre o seu dispositivo e a infraestrutura da <strong>P2 PRO</strong>.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Para dominar este processo, você precisa entender que o sistema é <em>case-sensitive</em> (diferencia maiúsculas de minúsculas) e extremamente sensível a caracteres especiais. Um ponto extra ou uma letra trocada invalidam instantaneamente a solicitação de login.
          </p>
        </div>
      )
    },
    {
      title: "2. O Padrão Xtream Codes API: O Guia de Campos",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/20 p-6 rounded-2xl border border-white/5">
              <h4 className="font-bold text-electric mb-4 flex items-center gap-2"><Globe className="w-5 h-5" /> URL / Host (O Endereço)</h4>
              <p className="text-sm text-muted-foreground mb-4">É o endereço físico do servidor na web. Deve sempre começar com <code>http://</code> (ou https) e geralmente termina com a porta, como <code>:8080</code>.</p>
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-xs text-red-400"><strong>Erro Comum:</strong> Esquecer o 'http://' ou colocar 'www' no início.</p>
              </div>
            </div>
            <div className="bg-black/20 p-6 rounded-2xl border border-white/5">
              <h4 className="font-bold text-electric mb-4 flex items-center gap-2"><Key className="w-5 h-5" /> Usuário e Senha</h4>
              <p className="text-sm text-muted-foreground mb-4">São suas chaves únicas. O usuário identifica sua linha no painel, e a senha garante que apenas você a utilize.</p>
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-xs text-red-400"><strong>Erro Comum:</strong> Confundir a letra 'O' com o número '0' ou 'I' com 'l'.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Passo a Passo Técnico para Entrada de Dados",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <div className="flex gap-6 items-start">
            <div className="w-10 h-10 rounded-full bg-electric flex items-center justify-center flex-shrink-0 font-black">1</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Preparação do Teclado Virtual</h4>
              <p className="text-muted-foreground">Em Smart TVs, digitar pelo controle remoto é lento e propenso a erros. Se possível, utilize um teclado USB ou o aplicativo de controle remoto do seu celular para garantir a precisão dos caracteres.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-10 h-10 rounded-full bg-electric flex items-center justify-center flex-shrink-0 font-black">2</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Verificação de Espaços Fantasmas</h4>
              <p className="text-muted-foreground">Ao copiar e colar dados do WhatsApp ou E-mail, o sistema muitas vezes adiciona um espaço vazio ao final do texto. No IPTV, esse espaço é lido como um caractere inválido. Certifique-se de apagar qualquer espaço extra após a senha ou a URL.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-10 h-10 rounded-full bg-electric flex items-center justify-center flex-shrink-0 font-black">3</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Protocolo de Confirmação</h4>
              <p className="text-muted-foreground">Após digitar, não tenha pressa em clicar em Login. Revise caractere por caractere. Um erro aqui significa ter que apagar e começar tudo de novo.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Solucionando Problemas de Autenticação",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg text-muted-foreground mb-6">Se mesmo após seguir o guia o login falhar, verifique estes pontos avançados:</p>
          <div className="bg-white/5 p-8 rounded-3xl border border-white/10 space-y-4">
            <div className="flex items-center gap-3 text-red-400">
              <AlertTriangle className="w-6 h-6" />
              <h5 className="font-bold">Bloqueio de DNS pelo Provedor</h5>
            </div>
            <p className="text-sm text-muted-foreground">Alguns provedores de internet (ISPs) bloqueiam domínios de IPTV. Tente trocar o DNS do seu dispositivo para o da Cloudflare (1.1.1.1) ou Google (8.8.8.8). Isso resolve 80% dos casos de 'Server Error'.</p>
            <hr className="border-white/10" />
            <div className="flex items-center gap-3 text-amber-400">
              <Settings className="w-6 h-6" />
              <h5 className="font-bold">Atualização do Player</h5>
              <p className="text-sm text-muted-foreground">Versões antigas de aplicativos podem ter bugs de autenticação. Certifique-se de estar usando a versão mais recente do <strong>One X</strong> ou <strong>P2 PLAY</strong>.</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Central de Ajuda IPTV"
        title="Como Inserir Dados de Acesso no IPTV: Guia Técnico Avançado"
        subtitle="Evite erros de login e garanta acesso imediato: aprenda a técnica correta de inserção de credenciais em qualquer player."
        heroImage="https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "A inserção dos dados de acesso é o portal para o seu entretenimento. Parece uma tarefa simples, mas a precisão técnica exigida pelos servidores de elite da P2 PRO não perdoa falhas de digitação.",
          "Neste guia denso, vamos além do básico 'copie e cole'. Explicamos a lógica por trás de cada campo, os erros invisíveis que travam seu login e as estratégias profissionais para garantir que você entre no sistema na primeira tentativa.",
          "Domine a arte da autenticação e nunca mais perca tempo com mensagens de 'Login Failed' ou 'Auth Error'."
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="🔑 Seus Dados não Estão Funcionando?"
        ctaDescription="Se você seguiu o guia e ainda tem problemas, sua linha pode precisar de um reset técnico. Fale com nossa central agora."
        ctaButtonText="Suporte Técnico Online"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/como-inserir-dados-acesso-iptv")({
  head: () => ({
    meta: [
      { title: "Como Inserir Dados de Acesso no IPTV - Guia Denso 2026" },
      { name: "description", content: "O tutorial definitivo sobre login IPTV. Aprenda a evitar erros de digitação, configurar DNS e entender a API Xtream Codes para um acesso sem falhas." },
    ],
  }),
  component: ArticlePage,
});
